import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Laptop, Plus, X, ArrowLeftRight, RotateCcw, KeyRound, Eye } from 'lucide-react';
import EmployeeSelect from '../../components/pickers/EmployeeSelect';

const CATEGORIES = ['Laptop', 'Desktop', 'Workstation', 'Monitor', 'Mobile', 'Tablet', 'SIM Card', 'Network Equipment', 'Printer', 'UPS', 'Keyboard/Mouse', 'Docking Station', 'Software Licence', 'Accessories', 'Other IT'];
const CONDITIONS = ['new', 'good', 'fair', 'poor', 'damaged', 'retired'];
const SOFTWARE = 'Software Licence';
const STATUS_COLORS = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  allocated: 'bg-blue-50 text-blue-700 border-blue-200',
  in_repair: 'bg-amber-50 text-amber-700 border-amber-200',
  retired: 'bg-slate-100 text-slate-500 border-slate-300',
  lost: 'bg-red-50 text-red-600 border-red-200',
};

const EMPTY = { category: 'Laptop', name: '', make_model: '', serial_number: '', purchase_date: '', purchase_cost: '', warranty_expiry: '', location: '', condition: 'good', notes: '', licence_key: '', licence_seats: '' };

export default function ITAssetRegister() {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState({ category: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [allocFor, setAllocFor] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    const params = {};
    if (filter.category) params.category = filter.category;
    if (filter.status) params.status = filter.status;
    apiGet('/it/assets', params).then((r) => setAssets(r.items || [])).catch(() => toast.error('Failed to load IT assets (need IT permission)'));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Asset name is required'); return; }
    const body = { ...form, purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : null };
    if (form.category === SOFTWARE) { body.licence_seats = form.licence_seats ? Number(form.licence_seats) : 1; }
    else { delete body.licence_key; delete body.licence_seats; }
    try {
      await apiPost('/it/assets', body);
      toast.success('IT asset added'); setShowForm(false); setForm(EMPTY); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const doReturn = async (a) => {
    if (!window.confirm(`Return ${a.name}?`)) return;
    try { const r = await apiPost(`/it/assets/${a.id}/return`, { return_condition: a.condition || 'good' });
      if (r.missing_flagged) toast.warning(`Returned — missing accessories: ${r.accessories_missing.join(', ')}`);
      else toast.success('Asset returned'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Return failed'); }
  };

  const revealKey = async (a) => {
    try { const r = await apiGet(`/it/assets/${a.id}/licence-key`); window.alert(`Licence key for ${a.name}:\n\n${r.licence_key}`); }
    catch (err) { toast.error('Could not reveal key'); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="it-asset-register">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Information Technology</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">IT Asset Register</h1>
          <p className="text-sm text-slate-600 mt-1">Laptops, mobiles, SIMs, software licences and accessories.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-it-asset-btn" className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2"><Plus size={14} /> Add Asset</button>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="it-filter-category">
          <option value="">All categories</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="border border-slate-200 rounded-sm px-3 py-1.5 text-xs" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="it-filter-status">
          <option value="">All statuses</option>{Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {assets.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="it-assets-empty"><Laptop size={40} className="mx-auto mb-3 opacity-40" /><p className="text-sm">No IT assets yet.</p></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Tag', 'Asset', 'Category', 'Status', 'Allocated / Seats', 'Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {assets.map((a) => {
                const isSw = a.category === SOFTWARE;
                const pct = isSw && a.licence_seats ? Math.round((a.seats_used / a.licence_seats) * 100) : 0;
                return (
                  <tr key={a.id} data-testid={`it-asset-row-${a.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{a.asset_tag}</td>
                    <td className="px-3 py-2">
                      <div className="font-display font-bold">{a.name}</div>
                      <div className="text-slate-400 text-[10px]">{a.make_model || a.serial_number || ''}</div>
                      {isSw && a.licence_key_masked && <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5"><KeyRound size={9} /> {a.licence_key_masked}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{a.category}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_COLORS[a.status]}`}>{a.status}</span></td>
                    <td className="px-3 py-2">
                      {isSw ? (
                        <div data-testid={`seats-${a.id}`}>
                          <div className="flex items-center justify-between text-[10px] text-slate-500"><span>{a.seats_used}/{a.licence_seats} seats</span><span className="font-bold">{a.seats_available} free</span></div>
                          <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden mt-1"><div className={`h-full ${pct >= 100 ? 'bg-red-400' : 'bg-[#3b4872]'}`} style={{ width: `${pct}%` }} /></div>
                        </div>
                      ) : (a.allocated_to_name || <span className="text-slate-300">—</span>)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {isSw && a.licence_key_masked && <button onClick={() => revealKey(a)} data-testid={`reveal-${a.id}`} className="text-slate-500 font-bold flex items-center gap-1"><Eye size={12} /> Key</button>}
                        {(isSw ? a.seats_available > 0 : a.status === 'available') && (
                          <button onClick={() => setAllocFor(a)} data-testid={`it-allocate-${a.id}`} className="text-[#3b4872] font-bold flex items-center gap-1"><ArrowLeftRight size={12} /> Allocate</button>
                        )}
                        {(a.status === 'allocated' || (isSw && a.seats_used > 0)) && (
                          <button onClick={() => doReturn(a)} data-testid={`it-return-${a.id}`} className="text-emerald-700 font-bold flex items-center gap-1"><RotateCcw size={12} /> Return</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="it-asset-form-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Add IT Asset</h3><button type="button" onClick={() => setShowForm(false)}><X size={18} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="lbl">Category</span><select className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="it-asset-category">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
              <label className="block"><span className="lbl">Condition</span><select className="inp" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>{CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            </div>
            <label className="block"><span className="lbl">Name *</span><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="it-asset-name" /></label>
            {form.category === SOFTWARE ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="lbl">Licence Key</span><input className="inp" value={form.licence_key} onChange={(e) => setForm({ ...form, licence_key: e.target.value })} data-testid="it-licence-key" placeholder="encrypted at rest" /></label>
                <label className="block"><span className="lbl">Seats</span><input type="number" min="1" className="inp" value={form.licence_seats} onChange={(e) => setForm({ ...form, licence_seats: e.target.value })} data-testid="it-licence-seats" /></label>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="lbl">Make / Model</span><input className="inp" value={form.make_model} onChange={(e) => setForm({ ...form, make_model: e.target.value })} /></label>
                <label className="block"><span className="lbl">Serial No.</span><input className="inp" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="lbl">Warranty Expiry</span><input type="date" className="inp" value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} /></label>
              <label className="block"><span className="lbl">Location</span><input className="inp" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></label>
            </div>
            <button type="submit" data-testid="it-asset-submit" className="w-full py-2.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c]">Add Asset</button>
          </form>
        </div>
      )}

      {allocFor && <AllocateModal asset={allocFor} onClose={() => setAllocFor(null)} onDone={() => { setAllocFor(null); load(); }} />}
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
    </div>
  );
}

function AllocateModal({ asset, onClose, onDone }) {
  const [emp, setEmp] = useState(null);
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [accessories, setAccessories] = useState('');
  const [busy, setBusy] = useState(false);
  const isSw = asset.category === 'Software Licence';
  const submit = async (e) => {
    e.preventDefault();
    if (!emp) { toast.error('Select an employee'); return; }
    setBusy(true);
    try {
      await apiPost(`/it/assets/${asset.id}/allocate`, {
        employee_id: emp.id, expected_return_date: date || null, handover_notes: notes,
        accessories_included: accessories.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Asset allocated'); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Allocation failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="it-allocate-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-sm w-full max-w-sm p-5 space-y-3">
        <h3 className="font-display font-bold text-sm">Allocate — {asset.name}</h3>
        <div><span className="lbl">Employee *</span><EmployeeSelect value={emp} onSelect={setEmp} testid="it-alloc-emp" /></div>
        {!isSw && (
          <>
            <label className="block"><span className="lbl">Accessories (comma separated)</span><input className="inp" value={accessories} onChange={(e) => setAccessories(e.target.value)} data-testid="it-alloc-accessories" placeholder="charger, carry bag, mouse" /></label>
            <label className="block"><span className="lbl">Expected Return Date</span><input type="date" className="inp" value={date} onChange={(e) => setDate(e.target.value)} data-testid="it-alloc-return-date" /></label>
          </>
        )}
        <label className="block"><span className="lbl">Handover Notes</span><textarea rows={2} className="inp" value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider">Cancel</button>
          <button type="submit" disabled={busy} data-testid="it-alloc-submit" className="flex-1 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Allocating…' : 'Allocate'}</button>
        </div>
        <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
      </form>
    </div>
  );
}
